import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from '@unform/web';

import { Painel } from '../../../../components/Painel';
import { Pagination } from '../../../../components/Pagination';
import { Icon } from '../../../../components/Icon';
import { Select, optionProps } from '../../../../components/Select';
import {
  EmptyState,
  Table,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../../components/Table';
import { colors } from '../../../../styles/tokens';
import { useModal } from '../../../../hooks/modal';
import { api } from '../../../../services/api';
import {
  AdminUsersService,
  IAdminUser,
} from '../../../../services/AdminUsersService';
import { AssociateGymUser } from '../../../../modais/associateGymUser';

import noAvatar from '../../../../assets/no-avatar.svg';

import {
  AddButton,
  Avatar,
  Container,
  Content,
  ExpandRow,
  ExpandToggle,
  FilterRow,
  GymRow,
  GymRowInfo,
  Header,
  IconButton,
  MemberCell,
  MemberEmail,
  MemberName,
  PageSubtitle,
  PageTitle,
  PlatformBadge,
  SearchIconWrapper,
  SearchInput,
  SearchWrapper,
  SelectWrapper,
  StatusBadge,
} from './styles';

interface IPermissionOption {
  code: string;
  description: string;
  scope: 'gym' | 'platform';
}

const PAGE_SIZE = 10;

const AdminUsers: React.FC = () => {
  const adminUsersService = new AdminUsersService();
  const navigate = useNavigate();

  const { addModal } = useModal();

  const [users, setUsers] = useState<IAdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [permissionCodes, setPermissionCodes] = useState<string[]>([]);
  const [permissionOptions, setPermissionOptions] = useState<optionProps[]>([]);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<IPermissionOption[]>('users/permissions/catalog')
      .then(response =>
        setPermissionOptions(
          response.data.map(permission => ({
            value: permission.code,
            label: permission.description,
          })),
        ),
      )
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, permissionCodes]);

  const getUsers = useCallback(() => {
    setLoading(true);
    adminUsersService
      .getUsers({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch,
        permission_codes: permissionCodes.length ? permissionCodes : undefined,
      })
      .then(response => {
        setUsers(response.data.data);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, permissionCodes]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const handleAssociate = useCallback(() => {
    addModal(<AssociateGymUser onSuccess={getUsers} />);
  }, [addModal, getUsers]);

  const toggleExpanded = useCallback((userId: string) => {
    setExpandedUserId(current => (current === userId ? null : userId));
  }, []);

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>Gestão de usuários</PageTitle>
            <PageSubtitle>
              Todos os usuários da plataforma, suas academias e permissões
            </PageSubtitle>
          </div>

          <AddButton type="button" onClick={handleAssociate}>
            <Icon name="person_add" size={18} color={colors.onPrimary} />
            Associar usuário
          </AddButton>
        </Header>

        <Form onSubmit={() => undefined}>
          <FilterRow>
            <SearchWrapper>
              <SearchIconWrapper>
                <Icon name="search" size={18} color={colors.onSurfaceVariant} />
              </SearchIconWrapper>
              <SearchInput
                type="text"
                placeholder="Buscar por nome, email ou username"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </SearchWrapper>

            <SelectWrapper>
              <Select
                name="permission_codes"
                options={permissionOptions}
                placeholder="Filtrar por permissão"
                isMulti
                onChange={(selected: any) =>
                  setPermissionCodes(
                    (selected || []).map((option: optionProps) => option.value),
                  )
                }
              />
            </SelectWrapper>
          </FilterRow>
        </Form>

        <Content>
          <TableWrapper>
            <Table>
              <Thead>
                <tr>
                  <Th>Usuário</Th>
                  <Th>Academias</Th>
                  <Th>Ações</Th>
                </tr>
              </Thead>
              <Tbody>
                {users.map(user => {
                  const isExpanded = expandedUserId === user.id;

                  return (
                    <React.Fragment key={user.id}>
                      <tr>
                        <Td>
                          <MemberCell>
                            <Avatar
                              src={user.avatar_url || noAvatar}
                              alt={user.fullname}
                            />
                            <div>
                              <MemberName>{user.fullname}</MemberName>
                              <MemberEmail>{user.email}</MemberEmail>
                              <StatusBadge active={user.is_active}>
                                {user.is_active ? 'Ativo' : 'Inativo'}
                              </StatusBadge>
                              {!!user.platformPermissions.length && (
                                <PlatformBadge>
                                  Admin de plataforma
                                </PlatformBadge>
                              )}
                            </div>
                          </MemberCell>
                        </Td>
                        <Td>
                          <ExpandToggle
                            type="button"
                            onClick={() => toggleExpanded(user.id)}
                            disabled={!user.memberships.length}
                          >
                            {user.memberships.length} academia
                            {user.memberships.length === 1 ? '' : 's'}
                            {!!user.memberships.length && (
                              <Icon
                                name={
                                  isExpanded ? 'expand_less' : 'expand_more'
                                }
                                size={18}
                                color={colors.onSurfaceVariant}
                              />
                            )}
                          </ExpandToggle>
                        </Td>
                        <Td>
                          <IconButton
                            type="button"
                            title="Ver detalhes do usuário"
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                          >
                            <Icon
                              name="chevron_right"
                              size={20}
                              color={colors.onSurfaceVariant}
                            />
                          </IconButton>
                        </Td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <Td colSpan={3}>
                            <ExpandRow>
                              {user.memberships.map(membership => (
                                <GymRow key={membership.id}>
                                  <GymRowInfo>
                                    {membership.gym.name}
                                    <span>
                                      {membership.permissions.length} permissões
                                    </span>
                                  </GymRowInfo>

                                  <IconButton
                                    type="button"
                                    title="Ver detalhes"
                                    onClick={() =>
                                      navigate(`/admin/users/${user.id}`)
                                    }
                                  >
                                    <Icon
                                      name="chevron_right"
                                      size={18}
                                      color={colors.onSurfaceVariant}
                                    />
                                  </IconButton>
                                </GymRow>
                              ))}
                            </ExpandRow>
                          </Td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </Tbody>
            </Table>

            {!users.length && (
              <EmptyState>Nenhum usuário encontrado.</EmptyState>
            )}
          </TableWrapper>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            itemLabel="usuários"
            onPageChange={setPage}
          />
        </Content>
      </Container>
    </Painel>
  );
};

export { AdminUsers };
